import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ArrowUpIcon, SmileIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { EmojiPicker } from '@/components/organisms/EmojiPicker/EmojiPicker';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isKeyEnter } from '@/util/util';

type Props = {
  handleButtonClick?: (e: React.MouseEvent<HTMLButtonElement>, value: string) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

export const SendMessageInput = ({
  handleButtonClick,
  onKeyUp,
  placeholder,
  disabled,
  className,
}: Props) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const selectionRef = useRef<number>(0);

  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  const handleSend = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!value) {
        return;
      }
      handleButtonClick?.(e, value);
      setValue('');
    },
    [value, handleButtonClick]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isKeyEnter(e) && !e.shiftKey) {
      e.preventDefault();
    }
  }, []);

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      onKeyUp?.(e);
      // Only clear when a send actually happens; while disabled (in flight) the
      // parent skips the send, so keep the text until the user can send it.
      if (isKeyEnter(e) && !e.shiftKey && !disabled) {
        setValue('');
      }
    },
    [onKeyUp, disabled]
  );

  const handleEmojiButtonClick = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Capture cursor position before the picker opens so emoji is inserted at the right place
      selectionRef.current = textarea.selectionStart ?? value.length;
    }
    setShowEmojiPicker((prev) => !prev);
  }, [value.length]);

  const handleEmojiClick = useCallback(
    ({ emoji }: { emoji: string }) => {
      const pos = selectionRef.current;
      const newValue = value.slice(0, pos) + emoji + value.slice(pos);
      setValue(newValue);
      setShowEmojiPicker(false);

      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          const newPos = pos + emoji.length;
          textarea.selectionStart = textarea.selectionEnd = newPos;
        }
      });
    },
    [value]
  );

  return (
    <div className="relative">
      {showEmojiPicker && (
        <div className="absolute bottom-full left-0 z-10 mb-2 h-50 w-full">
          <EmojiPicker
            onClickAway={(e) => {
              // Ignore clicks on the emoji button - let the button's onClick handle it
              if (emojiButtonRef.current?.contains(e.target as Node)) {
                return;
              }
              setShowEmojiPicker(false);
            }}
            onEmojiClick={handleEmojiClick}
            width="100%"
            height={200}
          />
        </div>
      )}
      <div
        className={cn(
          'flex flex-col gap-4 rounded-lg border border-border bg-background pt-3 pr-2 pb-2 pl-3 outline-none focus-within:ring-2 focus-within:ring-muted-foreground/20 focus-within:ring-offset-2',
          className
        )}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          placeholder={placeholder ?? t('try_it.modal.type_here')}
          rows={1}
          className="field-sizing-content max-h-28 resize-none overflow-y-auto bg-transparent text-base leading-6 outline-none placeholder:text-muted-foreground"
        />
        <div className="flex items-end justify-between">
          <div className="-ml-0.5 flex items-center gap-1">
            <Button
              ref={emojiButtonRef}
              type="button"
              variant="ghost-neutral"
              size="icon-sm"
              aria-label={t('emojiPicker.emoji_panel')}
              onClick={handleEmojiButtonClick}
              disabled={disabled}
            >
              <SmileIcon size={16} />
            </Button>
          </div>
          <Button
            type="button"
            variant="outline-neutral"
            size="sm"
            onClick={handleSend}
            disabled={disabled}
            className="min-w-16"
          >
            {t('common.send')}
            <ArrowUpIcon />
          </Button>
        </div>
      </div>
    </div>
  );
};
